import { applicationSummarySchema, type ApplicationSummary } from '@growup/streaming-shared';
import { cx } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson } from '../api';
import { AnnouncementsView } from './AnnouncementsView';
import { AttentionView, loadAttention } from './AttentionView';
import { ChannelView } from './ChannelView';
import { ChecklistItemsView } from './ChecklistItemsView';
import { MyAfilhadosView } from './MyAfilhadosView';
import { OverviewView } from './OverviewView';
import { PadrinhosView } from './PadrinhosView';
import { StreamerApplications } from './StreamerApplications';
import { StreamersView } from './StreamersView';
import { WallView } from './WallView';

type Tab =
  | 'resumo'
  | 'candidaturas'
  | 'streamers'
  | 'atencao'
  | 'padrinhos'
  | 'checklist'
  | 'afilhados'
  | 'anuncios'
  | 'mural'
  | 'canal';

type NavItem = { id: Tab; label: string; count?: number };
type NavGroup = { title: string; items: NavItem[] };

/**
 * Streaming (RF-F1; Gestor de Streaming, `streaming.manage`), arrumado num
 * menu em grupos (arrumar a gestao, parte 3): Hoje (Resumo), Pessoas,
 * Comunicacao e Configuracao. No computador o menu fica a esquerda; no
 * telemovel, em cima. `openAccountId` abre o streamer dessa conta. Um
 * Padrinho/Madrinha (`streaming.padrinho`, GS1) ve so o seu grupo; o
 * Programador do canal (`streaming.channel`, GS16) ve o Canal.
 */
export function StreamingSection({
  openAccountId = null,
  onOpenMember,
  manage = true,
  padrinho = false,
  channel = false,
}: {
  openAccountId?: string | null;
  onOpenMember?: ((accountId: string) => void) | undefined;
  manage?: boolean;
  padrinho?: boolean;
  channel?: boolean;
}) {
  const [tab, setTab] = useState<Tab>(
    !manage ? (padrinho ? 'afilhados' : 'canal') : openAccountId === null ? 'resumo' : 'streamers',
  );
  const [openStreamer, setOpenStreamer] = useState<string | null>(openAccountId);
  const [summary, setSummary] = useState<ApplicationSummary | null>(null);
  const [attention, setAttention] = useState(0);

  // Os contadores sao uma ajuda: se falharem, a seccao funciona sem eles.
  const loadSummary = useCallback(() => {
    if (!manage) return;
    getJson('/api/streaming/applications/summary', applicationSummarySchema)
      .then(setSummary)
      .catch(() => setSummary(null));
    loadAttention()
      .then((list) => setAttention(list.length))
      .catch(() => setAttention(0));
  }, [manage]);
  useEffect(loadSummary, [loadSummary]);

  const groups: NavGroup[] = manage
    ? [
        { title: 'Hoje', items: [{ id: 'resumo', label: 'Resumo' }] },
        {
          title: 'Pessoas',
          items: [
            { id: 'candidaturas', label: 'Candidaturas', count: summary?.pending ?? 0 },
            { id: 'streamers', label: 'Streamers' },
            { id: 'atencao', label: 'Precisa de atenção', count: attention },
            { id: 'padrinhos', label: 'Padrinhos' },
            ...(padrinho ? [{ id: 'afilhados' as const, label: 'Os meus afilhados' }] : []),
          ],
        },
        {
          title: 'Comunicação',
          items: [
            { id: 'anuncios', label: 'Anúncios' },
            { id: 'mural', label: 'Murais' },
          ],
        },
        {
          title: 'Configuração',
          items: [
            { id: 'checklist', label: 'Itens da checklist' },
            ...(channel ? [{ id: 'canal' as const, label: 'Canal' }] : []),
          ],
        },
      ]
    : [
        ...(padrinho
          ? [
              {
                title: 'O meu grupo',
                items: [
                  { id: 'afilhados' as const, label: 'Os meus afilhados' },
                  { id: 'anuncios' as const, label: 'Anúncios' },
                  { id: 'mural' as const, label: 'Mural do grupo' },
                ],
              },
            ]
          : []),
        ...(channel
          ? [{ title: 'Canal Grow uP', items: [{ id: 'canal' as const, label: 'Canal' }] }]
          : []),
      ];
  const items = groups.flatMap((g) => g.items);

  const go = (next: Tab) => {
    if (next !== 'streamers') setOpenStreamer(null);
    setTab(next);
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
      {items.length > 1 ? <StreamingNav groups={groups} current={tab} onChange={go} /> : null}
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {tab === 'resumo' ? (
          <OverviewView
            summary={summary}
            attention={attention}
            onGo={go}
            onOpenStreamer={(accountId) => {
              setOpenStreamer(accountId);
              setTab('streamers');
            }}
          />
        ) : null}
        {tab === 'candidaturas' ? <StreamerApplications onChanged={loadSummary} /> : null}
        {tab === 'streamers' ? (
          <StreamersView
            key={openStreamer ?? 'lista'}
            openAccountId={openStreamer}
            onOpenMember={onOpenMember}
          />
        ) : null}
        {tab === 'atencao' ? <AttentionView /> : null}
        {tab === 'padrinhos' ? <PadrinhosView /> : null}
        {tab === 'checklist' ? <ChecklistItemsView /> : null}
        {tab === 'afilhados' ? <MyAfilhadosView /> : null}
        {tab === 'anuncios' ? <AnnouncementsView mode={manage ? 'staff' : 'padrinho'} /> : null}
        {tab === 'mural' ? <WallView mode={manage ? 'staff' : 'padrinho'} /> : null}
        {tab === 'canal' ? <ChannelView /> : null}
      </div>
    </div>
  );
}

/**
 * O menu da seccao, em grupos. Cada entrada e um botao com o nome e, quando ha,
 * o numero do que espera (ex.: "Candidaturas (2)").
 */
function StreamingNav({
  groups,
  current,
  onChange,
}: {
  groups: NavGroup[];
  current: Tab;
  onChange: (tab: Tab) => void;
}) {
  return (
    <nav
      aria-label="Streaming"
      className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] lg:w-56 lg:shrink-0 lg:flex-col lg:gap-5 lg:overflow-visible lg:pb-0"
    >
      {groups.map((g) => (
        <div key={g.title} className="flex shrink-0 flex-col gap-1">
          <p className="px-3 font-sub text-caption font-bold uppercase text-gu-ink-muted">
            {g.title}
          </p>
          <ul className="flex gap-1 lg:flex-col">
            {g.items.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  aria-current={current === t.id ? 'page' : undefined}
                  onClick={() => onChange(t.id)}
                  className={cx(
                    'flex w-full items-center justify-between gap-3 whitespace-nowrap border-l-2 px-3 py-1.5 text-left',
                    current === t.id
                      ? 'border-gu-accent bg-gu-sunken text-gu-ink'
                      : 'border-transparent text-gu-ink-muted hover:text-gu-ink',
                  )}
                >
                  {t.count ? `${t.label} (${String(t.count)})` : t.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
